import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import _ from "lodash";
import CustomBreadCrumbs from "../../common/breadcrumbs";
import { TabMenu } from "primereact/tabmenu";
import {
  AccessScopeConstants,
  GlobalConstants,
  RouteConstants,
} from "../../../constants";
import * as RoleHelper from "../../../helpers/RoleHelper";
import { Button } from "primereact/button";
import EnterpriseForm from "./EnterpriseForm";
import SMTPForm from "./SMTPForm";
import Form from "../../common/form";
import {
  updateSelfEnterpriseRequest,
  requestEnterprise,
  enterpriseSMTPDetailsRequest,
  updateEnterpriseSMTPRequest,
  emailBannerDetailsRequest,
  updateEmailBannerRequest,
  learnerConfigurationRequest,
  updateLearnerConfigurationRequest,
  languageConfigurationRequest,
  updateLanguageConfigurationRequest,
} from "../../../store/slices/globalSlice";
import { uploadRequest } from "../../../store/slices/uploadSlice";
import { useMountEffect } from "primereact/hooks";
import EmailSettingsForm from "./EmailSettingsForm";
import LearningSettingsForm from "./LearnerSettingsForm";
import LanguageConfigurationForm from "./LanguageConfigurationForm";
import ReleaseNotes from "./ReleaseNotes";

const EnterpriseDetails = ({
  enterprise,
  user,
  uploadRequest,
  uploadDetails,
  updateSelfEnterpriseRequest,
  action,
  error,
  requesting,
  requestEnterprise,
  enterpriseSMTPDetailsRequest,
  updateEnterpriseSMTPRequest,
  emailBannerDetailsRequest,
  updateEmailBannerRequest,
  learnerConfigurationRequest,
  updateLearnerConfigurationRequest,
  languageConfigurationRequest,
  updateLanguageConfigurationRequest,
}) => {
  const intl = useIntl();
  const [isEditable, setIsEditable] = useState(false);
  const [fileName, setFileName] = useState("");
  const [bannerFileName, setBannerFileName] = useState("");
  const [headerFileName, setHeaderFileName] = useState("");
  const [footerFileName, setFooterFileName] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  const { role } = user || {};

  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.EnterpriseAccess,
    AccessScopeConstants.UpdateScope
  );

  useMountEffect(() => {
    enterpriseSMTPDetailsRequest();
    emailBannerDetailsRequest();
    learnerConfigurationRequest();
    languageConfigurationRequest();
  });

  const onLogoUpload = (event) => {
    const file = event.files[0];
    setFileName(file.name);
    const type = "logo";
    uploadRequest({ file, type });
  };

  const onBannerUpload = (event) => {
    const file = event.files[0];
    setBannerFileName(file.name);
    const type = "banner";
    uploadRequest({ file, type });
  };

  const onHeaderUpload = (event) => {
    const file = event.files[0];
    setHeaderFileName(file.name);
    const type = "image";
    uploadRequest({ file, type });
  };

  const onFooterUpload = (event) => {
    const file = event.files[0];
    setFooterFileName(file.name);
    const type = "image";
    uploadRequest({ file, type });
  };

  const handleUpdateEnterprise = (values) => {
    const payload = {
      name: values.name,
      brandColor: `#${values.brandColor.replace("#", "")}`,
      preLoginBanner:
        (bannerFileName && uploadDetails?.[bannerFileName]?.id) || null,
      artifactId: (fileName && uploadDetails?.[fileName]?.id) || null,
      preLoginMessage: values.preLoginMessage,
    };
    updateSelfEnterpriseRequest(payload);
  };

  useEffect(() => {
    if (action === "update" && !requesting && !error) {
      setIsEditable(false);
      requestEnterprise(
        {
          domain: enterprise?.subDomain,
          enterprise: enterprise,
          refresh: false,
        },
        true
      );
    }
    if (action === "update_smtp" && !requesting && !error) {
      setIsEditable(false);
      enterpriseSMTPDetailsRequest();
    }
    if (action === "update_email_banner" && !requesting && !error) {
      setIsEditable(false);
      emailBannerDetailsRequest();
    }

    if (action === "update_learner_configuration" && !requesting && !error) {
      setIsEditable(false);
      learnerConfigurationRequest();
    }

    if (action === "update_language_configuration" && !requesting && !error) {
      setIsEditable(false);
      languageConfigurationRequest();
    }
  }, [
    requestEnterprise,
    action,
    error,
    requesting,
    enterprise,
    enterpriseSMTPDetailsRequest,
    emailBannerDetailsRequest,
    learnerConfigurationRequest,
    languageConfigurationRequest,
  ]);

  const handleUpdateSMTP = (values) => {
    updateEnterpriseSMTPRequest(values);
  };

  const handleEmailSettingUpdate = () => {
    const payload = {
      emailFooterId:
        (footerFileName && uploadDetails?.[footerFileName]?.artifactId) || null,
      emailHeaderId:
        (headerFileName && uploadDetails?.[headerFileName]?.artifactId) || null,
    };
    updateEmailBannerRequest(payload);
  };

  const handleLearningConfigurationUpdate = (values) => {
    updateLearnerConfigurationRequest({
      ...values,
      canCustomiseDashboard: false,
      defaultDashboard:
        values.defaultDashboard?.value !== undefined
          ? values.defaultDashboard?.value
          : values.defaultDashboard,
    });
  };

  const handleLanguageConfigurationUpdate = (values) => {
    const paylaod = {
      ...values,
      allowedLanguages: _.uniq([
        ...values.allowedLanguages.map((lang) => lang.code),
        "en",
      ]),
    };

    updateLanguageConfigurationRequest(paylaod);
  };

  return (
    <div>
      <CustomBreadCrumbs
        labels={[
          {
            label: "enterprise_management",
            to: RouteConstants.APP_ROUTE_ENTERPRISE,
          },
        ]}
        navigate={() => {}}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "enterprise_management" })}
          </div>
          {(!isEditable && activeTab !== 5 && (
            <div className="edexlms-page-actions">
              <Button
                outlined
                icon="pi pi-pencil"
                onClick={() => setIsEditable(true)}
                size="small"
                label={intl.formatMessage({ id: "edit" })}
                type="button"
                disabled={!canUpdate}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate ? "" : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div className="edexlms-page-body">
          <TabMenu
            className="edexlms-tab-menu"
            model={[
              {
                label: intl.formatMessage({ id: "branding" }),
                command: () => {
                  if (activeTab !== 0) {
                    setActiveTab(0);
                    setIsEditable(false);
                  }
                },
              },
              {
                label: intl.formatMessage({ id: "smtp_settings" }),
                command: (e) => {
                  if (activeTab !== 1) {
                    setActiveTab(1);
                    setIsEditable(false);
                  }
                },
              },
              {
                label: intl.formatMessage({ id: "email_settings" }),
                command: (e) => {
                  if (activeTab !== 2) {
                    setActiveTab(2);
                    setIsEditable(false);
                  }
                },
              },
              {
                label: intl.formatMessage({ id: "learning_configurations" }),
                command: (e) => {
                  if (activeTab !== 3) {
                    setActiveTab(3);
                    setIsEditable(false);
                  }
                },
              },
              {
                label: intl.formatMessage({ id: "language_configurations" }),
                command: (e) => {
                  if (activeTab !== 4) {
                    setActiveTab(4);
                    setIsEditable(false);
                  }
                },
              },
              {
                label: intl.formatMessage({ id: "release_notes" }),
                command: (e) => {
                  if (activeTab !== 5) {
                    setActiveTab(5);
                    setIsEditable(false);
                  }
                },
              },
            ]}
          />
          {(activeTab === 0 && (
            <Form
              initialValues={{
                name: enterprise?.name,
                brandColor: enterprise?.brandColor || "3b82f6",
                preLoginMessage: enterprise?.preLoginMessage,
              }}
              disabled={!isEditable}
              submitBtn={{
                label: intl.formatMessage({
                  id: "update_enterprise",
                }),
                onClick: handleUpdateEnterprise,
              }}
              cancelBtn={{
                label: intl.formatMessage({
                  id: "cancel",
                }),
                onClick: (e) => setIsEditable(false),
              }}
            >
              <EnterpriseForm
                fileName={fileName}
                record={enterprise}
                disabled={!isEditable}
                onLogoUpload={onLogoUpload}
                onBannerUpload={onBannerUpload}
                bannerFileName={bannerFileName}
                uploadDetails={uploadDetails}
                onLogoClear={() => setFileName("")}
                onBannerClear={() => setBannerFileName("")}
              />
            </Form>
          )) ||
            null}
          {(activeTab === 1 && (
            <Form
              initialValues={{
                ...enterprise?.smtp,
                userName: "",
                password: "",
              }}
              record={enterprise.smtp}
              disabled={!isEditable}
              submitBtn={{
                label: intl.formatMessage({
                  id: "update_smtp",
                }),
                onClick: handleUpdateSMTP,
              }}
              cancelBtn={{
                label: intl.formatMessage({
                  id: "cancel",
                }),
                onClick: (e) => setIsEditable(false),
              }}
            >
              <SMTPForm disabled={!isEditable} />
            </Form>
          )) ||
            null}
          {(activeTab === 2 && (
            <Form
              disabled={!isEditable}
              submitBtn={{
                label: intl.formatMessage({
                  id: "update_email_settings",
                }),
                onClick: handleEmailSettingUpdate,
              }}
              cancelBtn={{
                label: intl.formatMessage({
                  id: "cancel",
                }),
                onClick: (e) => setIsEditable(false),
              }}
            >
              <EmailSettingsForm
                onFooterUpload={onFooterUpload}
                onHeaderUpload={onHeaderUpload}
                headerFileName={headerFileName}
                footerFileName={footerFileName}
                record={enterprise.emailSettings}
                disabled={!isEditable}
                uploadDetails={uploadDetails}
                onHeaderClear={() => setHeaderFileName("")}
                onFooterClear={() => setFooterFileName("")}
              />
            </Form>
          )) ||
            null}
          {(activeTab === 3 && (
            <Form
              initialValues={{
                ...enterprise.learningConfiguration?.learnerSettings,
                defaultDashboard:
                  enterprise.learningConfiguration?.learnerSettings
                    ?.defaultDashboard || "",
                customLabels: {
                  dashboard:
                    enterprise.learningConfiguration?.learnerSettings
                      ?.customLabels?.dashboard ||
                    intl.formatMessage({ id: "dashboard" }),
                  learningJourney:
                    enterprise.learningConfiguration?.learnerSettings
                      ?.customLabels?.learningJourney ||
                    intl.formatMessage({ id: "learning_journey" }),
                },
              }}
              disabled={!isEditable}
              submitBtn={{
                label: intl.formatMessage({
                  id: "update_learning_configuration",
                }),
                onClick: handleLearningConfigurationUpdate,
              }}
              cancelBtn={{
                label: intl.formatMessage({
                  id: "cancel",
                }),
                onClick: (e) => setIsEditable(false),
              }}
            >
              <LearningSettingsForm
                record={enterprise.learningConfiguration?.learnerSettings}
                disabled={!isEditable}
              />
            </Form>
          )) ||
            null}
          {(activeTab === 4 && (
            <Form
              initialValues={{
                allowedLanguages: GlobalConstants.LANGUAGES.filter((lang) =>
                  (
                    enterprise.languageConfiguration?.allowedLanguages || ["en"]
                  ).includes(lang.code)
                ).map((language) => ({
                  ...language,
                  id: language.code,
                  name: language.name,
                })),
              }}
              disabled={!isEditable}
              submitBtn={{
                label: intl.formatMessage({
                  id: "update_language_configuration",
                }),
                onClick: handleLanguageConfigurationUpdate,
              }}
              cancelBtn={{
                label: intl.formatMessage({
                  id: "cancel",
                }),
                onClick: (e) => setIsEditable(false),
              }}
            >
              <LanguageConfigurationForm disabled={!isEditable} />
            </Form>
          )) ||
            null}
          {(activeTab === 5 && <ReleaseNotes />) || null}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  enterprise: state?.global?.enterprise,
  user: state?.self?.user,
  uploadDetails: state.uploads?.details,
  action: state.global?.action,
  error: state.global?.error,
  requesting: state.global?.requesting,
});

const mapDispatchToProps = (dispatch) => ({
  uploadRequest: (payload) => dispatch(uploadRequest(payload)),
  updateSelfEnterpriseRequest: (payload) =>
    dispatch(updateSelfEnterpriseRequest(payload)),
  requestEnterprise: (data) => dispatch(requestEnterprise(data)),
  enterpriseSMTPDetailsRequest: () => dispatch(enterpriseSMTPDetailsRequest()),
  updateEnterpriseSMTPRequest: (data) =>
    dispatch(updateEnterpriseSMTPRequest(data)),
  emailBannerDetailsRequest: () => dispatch(emailBannerDetailsRequest()),
  updateEmailBannerRequest: (data) => dispatch(updateEmailBannerRequest(data)),
  learnerConfigurationRequest: (data) =>
    dispatch(learnerConfigurationRequest(data)),
  updateLearnerConfigurationRequest: (data) =>
    dispatch(updateLearnerConfigurationRequest(data)),
  languageConfigurationRequest: (data) =>
    dispatch(languageConfigurationRequest(data)),
  updateLanguageConfigurationRequest: (data) =>
    dispatch(updateLanguageConfigurationRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnterpriseDetails);
